import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { confirmResetPassword, confirmSignIn, signIn } from "aws-amplify/auth";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { useAlerts } from "./Alerts/AlertProvider";
import { SafeDialog } from "./Dialog";
import { FormTextField } from "./Forms/FormTextField";

export enum PasswordAction {
  Reset,
  NewPassword,
}

type PasswordDialogProps = {
  action: PasswordAction | null;
  email: string;
  open: boolean;
  onClose: () => void;
  initialCode?: string | null;
};

export function PasswordDialog({ action, email, open, onClose, initialCode }: PasswordDialogProps) {
  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<{
    code: string | null;
    password: string | null;
    passwordConfirmation: string | null;
  }>();

  const { code, password, passwordConfirmation } = watch();

  const alerts = useAlerts();
  const theme = useTheme();

  const passwordsMatch =
    password?.length && passwordConfirmation?.length && password === passwordConfirmation;
  const passwordHasLength = (password?.length ?? 0) >= 12;
  const passwordHasLowercase = /[a-z]/.test(password ?? "");
  const passwordHasUppercase = /[A-Z]/.test(password ?? "");
  const passwordHasNumber = /\d/.test(password ?? "");
  const passwordHasSymbol = /(_|[^\w\d ])/.test(password ?? "");

  const resettingPassword = action === PasswordAction.Reset;

  useEffect(() => {
    if (!open) return;
    if (!resettingPassword) return;
    if (!initialCode) return;

    setValue("code", initialCode);
  }, [open, resettingPassword, initialCode, setValue]);

  const disabled = useMemo(() => {
    if (resettingPassword && !(code?.length && password?.length && passwordConfirmation?.length))
      return true;

    if (
      ![
        passwordsMatch,
        passwordHasLength,
        passwordHasLowercase,
        passwordHasUppercase,
        passwordHasSymbol,
        passwordHasNumber,
      ].every((v) => !!v)
    )
      return true;

    return false;
  }, [
    code,
    password,
    passwordConfirmation,
    passwordsMatch,
    passwordHasLength,
    passwordHasLowercase,
    passwordHasUppercase,
    passwordHasSymbol,
    passwordHasNumber,
    resettingPassword,
  ]);

  if (action == null) return <></>;

  return (
    <SafeDialog open={open}>
      <form
        onSubmit={handleSubmit(async ({ code, password, passwordConfirmation }) => {
          if (!(password?.length && passwordConfirmation?.length)) return false;

          if (resettingPassword) {
            if (code == null) return false;

            try {
              await confirmResetPassword({
                username: email.toLowerCase(),
                confirmationCode: code.trim(),
                newPassword: password,
              });
              await signIn({ username: email.toLowerCase(), password });
              reset();
              onClose();
            } catch (err) {
              alerts.error("Error resetting password", err);
            }
            return;
          } else {
            if (!email) {
              alerts.error("User not provided.");
              return;
            }
            try {
              await confirmSignIn({ challengeResponse: password });
              reset();
              onClose();
            } catch (err) {
              alerts.error("Error creating new password", err);
            }
          }
        })}
      >
        <DialogTitle>{resettingPassword ? "Reset Password" : "Create New Password"}</DialogTitle>
        <DialogContent>
          <Stack>
            {resettingPassword && (
              <>
                <Typography paddingBottom={theme.spacing(1)}>
                  Enter the one-time code sent to your email to reset your password.
                </Typography>
                <FormTextField
                  control={control}
                  name="code"
                  label="One-time code"
                  inputProps={{ autoComplete: "one-time-code" }}
                  show1Pass
                />
              </>
            )}
            <Typography>Passwords must meet the following requirements:</Typography>
            <List>
              <ListItem>
                {passwordHasLength ? <Check color="success" /> : <Close color="error" />}
                <Typography>Password must be at least 12 characters</Typography>
              </ListItem>
              <ListItem>
                {passwordHasLowercase ? <Check color="success" /> : <Close color="error" />}
                <Typography>Password must have at least one lowercase letter</Typography>
              </ListItem>
              <ListItem>
                {passwordHasUppercase ? <Check color="success" /> : <Close color="error" />}
                <Typography>Password must have at least one uppercase letter</Typography>
              </ListItem>
              <ListItem>
                {passwordHasSymbol ? <Check color="success" /> : <Close color="error" />}
                <Typography>Password must have at least one symbol</Typography>
              </ListItem>
              <ListItem>
                {passwordHasNumber ? <Check color="success" /> : <Close color="error" />}
                <Typography>Password must have at least one number</Typography>
              </ListItem>
              <ListItem>
                {passwordsMatch ? <Check color="success" /> : <Close color="error" />}
                <Typography>Passwords must match</Typography>
              </ListItem>
            </List>
            <FormTextField
              type="password"
              control={control}
              name="password"
              label="Password"
              show1Pass
              inputProps={{ autoComplete: "new-password" }}
            />
            <FormTextField
              type="password"
              control={control}
              name="passwordConfirmation"
              label="Confirm Password"
              show1Pass
              inputProps={{ autoComplete: "new-password" }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {resettingPassword && (
            <Button
              onClick={() => {
                onClose();
                reset();
              }}
              variant="outlined"
            >
              Cancel
            </Button>
          )}
          <Button type="submit" loading={isSubmitting} disabled={disabled} variant="contained">
            {resettingPassword ? "Confirm Password Reset" : "Confirm New Password"}
          </Button>
        </DialogActions>
      </form>
    </SafeDialog>
  );
}
